import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button } from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import {
  DocumentCraftingDuplicatePrefill,
  DocumentCraftingTypeKey,
} from "../../../Models/documentCrafting";
import {
  useDocumentCraftingDuplicatePrefill,
  useDocumentCraftingTypes,
} from "../../../Query/useDocumentCrafting";
import {
  ErrorState,
  LoadingState,
  PageHeader,
} from "../Common/DocumentCraftingUi";
import {
  documentCraftingPaths,
  parseDuplicateParam,
} from "../Common/documentCraftingNavigation";
import DocumentWizard from "./DocumentWizard";
import TypePicker from "./TypePicker";

const BackToList: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  return (
    <Button
      color="secondary"
      startIcon={<ArrowBackRounded />}
      onClick={() => navigate(documentCraftingPaths.list)}
    >
      {t("nav.backToList")}
    </Button>
  );
};

/** The wizard for a type key, once the type registry has loaded. */
const TypeWizard: React.FC<{
  typeKey: string;
  duplicate?: DocumentCraftingDuplicatePrefill | null;
  onChangeType?: () => void;
}> = ({ typeKey, duplicate, onChangeType }) => {
  const { t } = useTranslation("documentCrafting");
  const types = useDocumentCraftingTypes();
  if (types.isLoading) return <LoadingState />;
  if (types.isError || !types.data) {
    return <ErrorState onRetry={() => types.refetch()} />;
  }
  const definition = types.data.types.find(
    (item) =>
      item.enabled &&
      (duplicate ? item.id === duplicate.prefill.type : item.key === typeKey),
  );
  if (!definition) {
    return (
      <>
        <PageHeader title={t("create.picker.title")} actions={<BackToList />} />
        <Alert severity="warning">{t("create.picker.unknownType")}</Alert>
      </>
    );
  }
  return (
    <DocumentWizard
      definition={definition}
      duplicate={duplicate}
      onChangeType={onChangeType}
    />
  );
};

const DuplicateWizard: React.FC<{ documentId: number; revisionId: number }> = ({
  documentId,
  revisionId,
}) => {
  const prefill = useDocumentCraftingDuplicatePrefill(documentId, revisionId);
  if (prefill.isLoading) return <LoadingState />;
  if (prefill.isError || !prefill.data) {
    return <ErrorState onRetry={() => prefill.refetch()} />;
  }
  return <TypeWizard typeKey="" duplicate={prefill.data} />;
};

/**
 * `/document-crafting/new`: the type picker, then the wizard of the chosen
 * type (`?type=<key>`), or the prefilled wizard of
 * `?duplicate=<documentId>:<revisionId>`.
 */
const CreateDocument: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const [params, setParams] = useSearchParams();
  const typeKey = params.get("type");
  const duplicate = parseDuplicateParam(params.get("duplicate"));

  if (duplicate) {
    return (
      <DuplicateWizard
        key={`${duplicate.documentId}:${duplicate.revisionId}`}
        documentId={duplicate.documentId}
        revisionId={duplicate.revisionId}
      />
    );
  }

  if (typeKey) {
    return (
      <TypeWizard
        key={typeKey}
        typeKey={typeKey}
        onChangeType={() => setParams({})}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={t("create.picker.title")}
        subtitle={t("create.picker.subtitle")}
        actions={<BackToList />}
      />
      <TypePicker
        onSelect={(key: DocumentCraftingTypeKey) => setParams({ type: key })}
      />
    </>
  );
};

export default CreateDocument;
